'use client'

import type {ReactNode} from 'react'
import {Input} from '@fitro/customer-ui/Input'
import {useTranslation} from '../../../hooks/useTranslation'
import type {GuestContactErrors, GuestContactField} from '../../../lib/guest-contact-validation'

export type GuestContactValues = {firstName: string; lastName: string; email: string; phone: string}
export type GuestConsents = {studioTermsAccepted: boolean; fitroTermsAccepted: boolean; marketingConsent: boolean}
export type GuestGender = '' | 'FEMALE' | 'MALE'

const CONTACT_FIELDS = [
    {field: 'firstName', type: 'text', autoComplete: 'given-name'},
    {field: 'lastName', type: 'text', autoComplete: 'family-name'},
    {field: 'email', type: 'email', autoComplete: 'email'},
    {field: 'phone', type: 'tel', autoComplete: 'tel'},
] as const
const GENDERS = [{value: 'FEMALE', key: 'female'}, {value: 'MALE', key: 'male'}] as const

const REQUIRED_MARK = <span className="ml-1 text-error-500" aria-hidden="true">*</span>
const LINK_CLASS = 'font-semibold text-primary-600 underline-offset-2 hover:underline dark:text-primary-400'
const CONSENT_CLASS = 'flex cursor-pointer items-start gap-3 text-sm leading-5 text-gray-700 dark:text-gray-200'
const CHECKBOX_CLASS = 'mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800'

/**
 * The contact, optional gender and consents of both Guest Checkout forms (classes/events and
 * individual training): the studio's and Fitro's terms are required, news from the studio is not.
 * Each error shows under its control.
 */
export function GuestContactFields({
    idPrefix,
    contact,
    onContactChange,
    consents,
    onConsentChange,
    errors,
    policies,
    gender = null,
}: {
    idPrefix: string
    contact: GuestContactValues
    onContactChange: (field: keyof GuestContactValues, value: string) => void
    consents: GuestConsents
    onConsentChange: (field: keyof GuestConsents, checked: boolean) => void
    errors: GuestContactErrors
    policies: {termsUrl?: string | null; privacyUrl?: string | null; fitroTermsUrl?: string | null}
    /** Asked only when the class shows participants split by gender. */
    gender?: {value: GuestGender; onChange: (value: Exclude<GuestGender, ''>) => void} | null
}) {
    const {t} = useTranslation('landing')
    const errorText = (field: GuestContactField) => {
        const key = errors[field]
        return key ? t(`publicEntry.guestCheckout.fieldErrors.${key}`) : undefined
    }
    const invalidProps = (field: GuestContactField) => errors[field]
        ? {'aria-invalid': true, 'aria-describedby': `${idPrefix}-${field}-error`}
        : {}
    // Same look as the shared Input's error text, for controls that are not an Input.
    const fieldError = (field: GuestContactField, className = '') => {
        const message = errorText(field)
        return message ? (
            <p id={`${idPrefix}-${field}-error`} className={`mt-2 text-caption text-error-600 dark:text-error-400 ${className}`}>
                {message}
            </p>
        ) : null
    }
    const requiredConsent = (field: 'studioTermsAccepted' | 'fitroTermsAccepted', text: ReactNode) => (
        <div>
            <label className={CONSENT_CLASS}>
                <input
                    type="checkbox"
                    name={field}
                    className={CHECKBOX_CLASS}
                    checked={consents[field]}
                    onChange={(event) => onConsentChange(field, event.target.checked)}
                    required
                    {...invalidProps(field)}
                />
                <span>{text}{REQUIRED_MARK}</span>
            </label>
            {fieldError(field, 'pl-7')}
        </div>
    )

    return (
        <>
            <div className="grid gap-4 sm:grid-cols-2">
                {CONTACT_FIELDS.map(({field, type, autoComplete}) => (
                    <Input
                        key={field}
                        id={`${idPrefix}-${field}`}
                        name={field}
                        type={type}
                        autoComplete={autoComplete}
                        label={t(`publicEntry.guestCheckout.${field}`)}
                        required
                        value={contact[field]}
                        onChange={(event) => onContactChange(field, event.target.value)}
                        errorText={errorText(field)}
                    />
                ))}
                {gender ? (
                    <fieldset className="m-0 min-w-0 border-0 p-0 sm:col-span-2" {...invalidProps('gender')}>
                        <legend className={`mb-1 block text-xs font-medium ${errors.gender ? 'text-error-600 dark:text-error-400' : 'text-gray-700 dark:text-gray-200'}`}>
                            {t('publicEntry.guestCheckout.gender')}{REQUIRED_MARK}
                        </legend>
                        <div className="grid grid-cols-2 gap-4">
                            {GENDERS.map((option) => (
                                <label
                                    key={option.value}
                                    className={`flex cursor-pointer items-center gap-3 rounded-lg border bg-white px-4 py-3 text-body leading-[var(--lh-body)] text-gray-900 transition-colors has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50 dark:bg-gray-800 dark:text-gray-100 dark:has-[:checked]:bg-primary-950/30 ${errors.gender ? 'border-error-900 dark:border-error-400' : 'border-gray-300 hover:border-gray-400 dark:border-gray-600'}`}
                                >
                                    <input
                                        type="radio"
                                        name="gender"
                                        className="h-4 w-4 shrink-0 border-gray-300 text-primary-600 focus:ring-primary-500"
                                        checked={gender.value === option.value}
                                        onChange={() => gender.onChange(option.value)}
                                    />
                                    {t(`publicEntry.guestCheckout.genders.${option.key}`)}
                                </label>
                            ))}
                        </div>
                        {fieldError('gender')}
                    </fieldset>
                ) : null}
            </div>
            <div className="space-y-3">
                {requiredConsent('studioTermsAccepted', <>
                    {t('publicEntry.guestCheckout.studioTerms')}{' '}
                    <a className={LINK_CLASS} href={policies.termsUrl ?? undefined} target="_blank" rel="noreferrer">{t('publicEntry.guestCheckout.studioTermsLink')}</a>{' '}
                    {t('publicEntry.guestCheckout.termsAnd')}{' '}
                    <a className={LINK_CLASS} href={policies.privacyUrl ?? undefined} target="_blank" rel="noreferrer">{t('publicEntry.guestCheckout.privacyLink')}</a>
                </>)}
                {requiredConsent('fitroTermsAccepted', <>
                    {t('publicEntry.guestCheckout.fitroTerms')}{' '}
                    <a className={LINK_CLASS} href={policies.fitroTermsUrl ?? undefined} target="_blank" rel="noreferrer">{t('publicEntry.guestCheckout.fitroTermsLink')}</a>
                </>)}
                <label className={CONSENT_CLASS}>
                    <input
                        type="checkbox"
                        name="marketingConsent"
                        className={CHECKBOX_CLASS}
                        checked={consents.marketingConsent}
                        onChange={(event) => onConsentChange('marketingConsent', event.target.checked)}
                    />
                    <span>{t('publicEntry.guestCheckout.marketing')}</span>
                </label>
            </div>
        </>
    )
}
